import { z } from "zod/v4";
import {
  CreateContactMutationRequest,
  contactValueLabelDtoSchema,
  createContactDtoSchema,
} from "@shared/generated/crm";
import {
  CONTACT_FIELD_DEFINITIONS,
  multiValueToItems,
  toSaferExternalHref,
} from "@features/Contacts/components/ContactBriefingHeader/contact-fields-helpers";
import {
  buildFacebookLink,
  buildInstagramLink,
  buildLinkedInLink,
  normalizeSocialHandleFields,
} from "@features/Contacts/components/ContactBriefingHeader/contact-summary-chips-helpers";

export const newContactFormSchema = createContactDtoSchema
  .extend({
    firstName: z.string().trim().min(1, "First name is required"),
    email: z
      .array(
        contactValueLabelDtoSchema.extend({
          value: z.email("Enter a valid email"),
        }),
      )
      .optional(),
    links: z
      .array(
        contactValueLabelDtoSchema.extend({
          value: z
            .string()
            .refine(
              (value) => toSaferExternalHref(value) !== null,
              "Enter a valid link",
            ),
        }),
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    if ((data.lastName?.trim().length ?? 0) > 0) return;
    if ((data.fullName?.trim().length ?? 0) > 0) return;
    ctx.addIssue({
      code: "custom",
      message: "Add a last name, or fill in Full Name instead",
      path: ["lastName"],
    });
    ctx.addIssue({
      code: "custom",
      message: "Add a full name, or fill in Last Name instead",
      path: ["fullName"],
    });
  });

type NewContactFieldValues = Record<string, string>;

export const NEW_CONTACT_DEFAULT_VALUES: NewContactFieldValues =
  Object.fromEntries(
    CONTACT_FIELD_DEFINITIONS.map((definition) => [definition.id, ""]),
  );

const toContactValueLabelItems = (value: string) =>
  multiValueToItems(value).map((item) => ({ value: item }));

export const toCreateContactBody = (
  fieldValues: NewContactFieldValues,
): CreateContactMutationRequest => {
  const { instagramHandle, linkedinHandle, facebookHandle } =
    normalizeSocialHandleFields(fieldValues);

  return {
    firstName: fieldValues.firstName ?? "",
    lastName: fieldValues.lastName ?? "",
    fullName: fieldValues.fullName ?? "",
    preferredName: fieldValues.preferredName ?? "",
    company: fieldValues.company ?? "",
    title: fieldValues.jobTitle ?? "",
    timezone: fieldValues.timezone ?? "",
    address: fieldValues.address ?? "",
    city: fieldValues.city ?? "",
    state: fieldValues.state ?? "",
    postalCode: fieldValues.postalCode ?? "",
    email: toContactValueLabelItems(fieldValues.email ?? ""),
    phone: toContactValueLabelItems(fieldValues.phone ?? ""),
    links: [
      ...toContactValueLabelItems(fieldValues.links ?? ""),
      ...(instagramHandle
        ? [{ value: buildInstagramLink(instagramHandle) }]
        : []),
      ...(linkedinHandle ? [{ value: buildLinkedInLink(linkedinHandle) }] : []),
      ...(facebookHandle ? [{ value: buildFacebookLink(facebookHandle) }] : []),
    ],
  };
};
